export type Sender = 'user' | 'ai' | 'system';

// Auth Types
export type AuthProvider = 'google' | 'discord';

export type SubscriptionTier = 'free' | 'pro' | 'max';

export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture: string;
  username?: string; // Custom username chosen by user
  provider: 'google';
  isPro?: boolean; // Pro subscription status (deprecated, use subscriptionTier)
  isMax?: boolean; // Max subscription status (deprecated, use subscriptionTier)
  subscriptionTier?: SubscriptionTier; // Current subscription tier
}

export interface DiscordUser {
  id: string;
  email: string;
  name: string;
  picture: string;
  username?: string; // Custom username chosen by user
  provider: 'discord';
  discriminator?: string; // Discord discriminator (e.g., #1234)
  isPro?: boolean; // Pro subscription status (deprecated, use subscriptionTier)
  isMax?: boolean; // Max subscription status (deprecated, use subscriptionTier)
  subscriptionTier?: SubscriptionTier; // Current subscription tier
}

export type User = GoogleUser | DiscordUser;

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export interface Message {
  id: string;
  text: string;
  sender: Sender;
  timestamp: number;
}

export interface ReplayAction {
  type: 'word' | 'message';
  word?: string;
  message?: Message;
  timestamp: number;
}

export enum GameState {
  START = 'START',
  CHOOSING_MODE = 'CHOOSING_MODE',
  SETTINGS = 'SETTINGS',
  CHOOSING_STORY = 'CHOOSING_STORY',
  PLAYING = 'PLAYING',
  GAME_OVER = 'GAME_OVER',
  LOADING_INITIAL = 'LOADING_INITIAL',
  // Multiplayer states
  MULTIPLAYER_LOBBY = 'MULTIPLAYER_LOBBY',
  MULTIPLAYER_WAITING = 'MULTIPLAYER_WAITING',
}

export type GameOutcome = 'win' | 'lose' | 'alternative_win' | null;

export interface WinCondition {
  objective: string;
  hint: string;
}

// Difficulty levels: 1 = Easy, 2 = Medium, 3 = Hard, 4 = Pure Chaos
export type Difficulty = 1 | 2 | 3 | 4;

// Genre options for story generation
export type Genre = 'random' | 'fantasy' | 'sci-fi' | 'horror' | 'comedy' | 'mystery' | 'romance' | 'western' | 'post-apocalyptic';

// Daily challenge data
export interface DailyChallenge {
  date: string; // YYYY-MM-DD format
  seed: string;
  genre: Genre;
  difficulty: Difficulty;
}

// Rewind penalty types
export type RewindPenalty = 
  | { type: 'word_ban'; bannedWord: string }
  | { type: 'chaos_suggestion'; description: string }
  | { type: 'narrator_sass'; message: string }
  | { type: 'item_loss'; item: string };

export interface AlternatePath {
  description: string;
  triggerCondition: string;
}

export interface AdventurePlan {
  estimatedTurns: number;
  mainPath: string[];
  alternatePaths: AlternatePath[];
  difficultyFactors: string[];
}

export type GameMode = 'adventure' | 'endless';

export interface StoryOption {
  id: string;
  preview: string;
  fullPrompt: string;
  winCondition?: WinCondition;
  adventurePlan?: AdventurePlan;
  genre?: Genre;
}

export interface RewindState {
  messages: Message[];
  replayActions: ReplayAction[];
  currentInput: string;
  suggestions: string[];
  countdown: { remaining: number; label: string } | null;
  penaltiesApplied: number;
}

export interface SuggestionResponse {
  suggestions: string[];
}

export type ModelOption = 'gemini/gemini-2.5-flash-lite' | 'openrouter/gpt-oss-120b' | 'groq/gpt-oss-120b' | 'cerebras/zai-glm-4.6';

export interface ModelSelection {
  chatModel: ModelOption;
  predictionModel: ModelOption;
}

export interface StoryResponse {
  text: string;
  outcome?: GameOutcome;
  outcomeReason?: string;
  countdown?: CountdownUpdate;
}

export interface CountdownUpdate {
  action: 'start' | 'decrement' | 'cancel';
  remaining?: number;
  label?: string;
}

export interface SavedAdventure {
  id: string;
  userId: string;
  timestamp: number;
  status: 'in_progress' | 'completed';
  gameMode: GameMode;
  difficulty: Difficulty;
  genre?: Genre;
  isDailyChallenge: boolean;
  dailyChallengeDate?: string;
  
  // Game state
  messages: Message[];
  replayActions: ReplayAction[];
  currentInput: string;
  suggestions: string[];
  
  // Win condition & plan
  winCondition?: WinCondition;
  adventurePlan?: AdventurePlan;
  
  // Outcome (for completed games)
  gameOutcome?: GameOutcome;
  outcomeReason?: string;
  
  // Stats
  turns: number;
  rewindPenalties: number;
  
  // Countdown state
  countdown?: { remaining: number; label: string };
}

// ============================================================================
// MULTIPLAYER TYPES
// ============================================================================

// Multiplayer turn mode
export type MultiplayerTurnMode = 'per_message' | 'per_prediction';

// Multiplayer player info
export interface MultiplayerPlayer {
  id: string;
  name: string;
  picture: string;
  username?: string;
  isHost: boolean;
  isActive: boolean; // Currently connected
}

// Multiplayer session state
export type MultiplayerSessionState = 'waiting' | 'playing' | 'voting' | 'ended';

// Emoji reaction for multiplayer
export interface EmojiReaction {
  id: string;
  emoji: string;
  playerId: string;
  playerName: string;
  timestamp: number;
}

// Adventure vote for multiplayer voting phase
export interface AdventureVote {
  optionId: string;
  playerId: string;
  playerName: string;
  timestamp: number;
}

// Voting state for adventure selection
export interface VotingState {
  options: StoryOption[];
  votes: AdventureVote[];
  votingEndTime: number; // Timestamp when voting ends
  selectedOptionId?: string; // Set after voting ends
}

// Multiplayer session
export interface MultiplayerSession {
  id: string;
  hostId: string;
  players: MultiplayerPlayer[];
  turnMode: MultiplayerTurnMode;
  currentTurnPlayerId: string;
  state: MultiplayerSessionState;
  gameMode: GameMode;
  difficulty: Difficulty;
  genre: Genre;
  createdAt: number;
  
  // Game state (shared across players)
  messages: Message[];
  currentInput: string;
  suggestions: string[];
  winCondition?: WinCondition;
  adventurePlan?: AdventurePlan;
  emojiReactions?: EmojiReaction[];
  
  // Voting settings (for adventure selection between games)
  votingEnabled: boolean; // If true, players vote for next adventure
  votingState?: VotingState;
  
  // Auto-send timer (in seconds, 0 = disabled)
  autoSendLimit: number;
  autoSendStartTime?: number; // When current player's turn started
  predictionsReady?: boolean; // Whether predictions/suggestions are loaded and ready
  
  // Max players (default: 10, minimum: 2)
  maxPlayers: number;
}

// Real-time update events for multiplayer
export type MultiplayerEventType = 
  | 'player_joined'
  | 'player_left'
  | 'player_disconnected'
  | 'game_started'
  | 'turn_changed'
  | 'input_updated'
  | 'suggestions_updated'
  | 'predictions_ready'
  | 'message_sent'
  | 'ai_response'
  | 'ai_streaming_chunk'
  | 'ai_streaming_complete'
  | 'game_over'
  | 'session_ended'
  | 'emoji_reaction'
  | 'voting_started'
  | 'vote_cast'
  | 'voting_ended'
  | 'auto_send_tick';

export interface MultiplayerEvent {
  type: MultiplayerEventType;
  sessionId: string;
  timestamp: number;
  playerId?: string;
  data?: Record<string, unknown>;
}

// Multiplayer message with player attribution
export interface MultiplayerMessage extends Message {
  playerId: string;
  playerName: string;
}
